import { useEffect, useMemo, useState } from "react";
import { Sparkles, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PlayerRow } from "@/components/PlayerRow";

export default function Leaderboard() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/players");
      setPlayers(res.data);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000); // refresh leaderboard view every minute
    return () => clearInterval(interval);
  }, []);

  const stats = useMemo(() => {
    const totalGames = players.reduce((s, p) => s + (p.plays || 0), 0);
    const avgPlacement =
      players.length === 0
        ? 0
        : players.reduce((s, p) => s + (p.avg_placement || 0), 0) / players.length;
    return { count: players.length, totalGames, avgPlacement };
  }, [players]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-[#2A2A30] bg-[#121214] p-6 sm:p-8 md:p-12 mb-8 md:mb-10">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/13129482/pexels-photo-13129482.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A0A0B] via-[#0A0A0B]/85 to-transparent pointer-events-none" />

        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] text-[#C8AA6E] font-bold mb-4 sm:mb-5">
            <Sparkles className="h-3 w-3" /> Set 17 · Tournament Tracker
          </div>
          <h1 className="font-[Outfit] text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#F0E6D2] mb-3 leading-[1.05]">
            Tabela <span className="text-[#C8AA6E]">turnieju</span> TFT
          </h1>
          <p className="text-[#A09B8C] max-w-2xl text-sm sm:text-base">
            Trzech graczy, jeden cel - Challenger na EUNE. Uczestnicy startują jednocześnie, 
            a wygrywa ten, kto jako pierwszy osiągnie najwyższą rangę w TFT. 
            Poniżej znajdziesz aktualne statystyki, LP oraz postępy każdego z uczestników.
            {/* Śledzimy rangę, LP, średnie miejsce i winrate uczestników turnieju.
            Statystyki ciągniemy z <span className="text-[#0AC8B9] font-semibold">lolchess.gg</span> i
            odświeżamy z panelu admina po każdej rundzie. */}
          </p><br></br>
          <bold>KARA</bold> dla przegranych: Zerowanie syropu malinowego
          <br></br>
          <p className="text-[#A09B8C] max-w-2xl text-sm sm:text-base">
            <a href="https://forms.gle/6zKL7UuxiWxN6twL9" target="_blank" className="text-[#0AC8B9] font-semibold">Wypełnij Pickem</a>
          </p>

          <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-2 sm:gap-6 max-w-xl">
            <StatTile icon={Users} label="Graczy" value={stats.count} testid="stat-count" />
            <StatTile
              label="Gier"
              value={stats.totalGames.toLocaleString("pl-PL")}
              testid="stat-games"
            />
            <StatTile
              label="Śr. miejsce"
              value={stats.avgPlacement ? stats.avgPlacement.toFixed(2) : "—"}
              testid="stat-avg"
              accent
            />
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <div className="rounded-xl border border-[#2A2A30] bg-[#121214] overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6E6A5E] font-bold border-b border-[#2A2A30] bg-[#0A0A0B]/40">
          <div className="col-span-1">#</div>
          <div className="col-span-4">Gracz</div>
          <div className="col-span-3">Ranga</div>
          <div className="col-span-4 grid grid-cols-4 gap-2 text-right">
            <div>Gier</div>
            <div>Śr. miej.</div>
            <div>Top 4</div>
            <div>Wygrane</div>
          </div>
        </div>

        {error && (
          <div className="p-6 text-[#E84057] text-sm" data-testid="error-message">
            {error}
          </div>
        )}

        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
        ) : players.length === 0 ? (
          <EmptyState />
        ) : (
          players.map((player, index) => (
            <PlayerRow key={player.id} player={player} index={index} />
          ))
        )}
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-[#2A2A30]/80 bg-[#0A0A0B]/40 px-3 sm:px-4 py-2.5 sm:py-3"
    >
      <div className="flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#6E6A5E] font-bold">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div
        className={`mt-1 font-[Outfit] text-xl sm:text-3xl font-black tracking-tight tabular-nums ${
          accent ? "text-[#0AC8B9]" : "text-[#F0E6D2]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="px-5 py-4 border-b border-[#2A2A30]/60 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="h-9 w-9 rounded-md bg-[#1A1A1D]" />
        <div className="h-11 w-11 rounded-md bg-[#1A1A1D]" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 bg-[#1A1A1D] rounded" />
          <div className="h-3 w-44 bg-[#1A1A1D] rounded" />
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-16 text-center" data-testid="empty-state">
      <div className="inline-flex items-center justify-center h-14 w-14 rounded-xl bg-[#0AC8B9]/10 border border-[#0AC8B9]/30 text-[#0AC8B9] mb-4">
        <Users className="h-6 w-6" />
      </div>
      <h3 className="font-[Outfit] text-xl font-bold text-[#F0E6D2] mb-2">
        Brak graczy w turnieju
      </h3>
      <p className="text-sm text-[#A09B8C] max-w-md mx-auto">
        Zaloguj się jako admin i dodaj pierwszych uczestników z lolchess.gg.
      </p>
    </div>
  );
}
